import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { HttpContext } from '@angular/common/http';
import { Component, computed, inject, Injector, Input, OnInit, signal, type SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@modules/alert/services/alert.service';
import { type AlertButtonConfig } from '@modules/alert/types/config';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { MAILBOX_TENANT_ID, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { type WorkspaceIdentity, WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { Tab } from '@modules/common/types/tab';
import { type DragData, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { FilesListStateService } from '@modules/files/services/files-list-state.service';
import { FilesService } from '@modules/files/services/files.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { FilesAppStateService } from '@modules/files/services/state.service';
import { FilesFilters } from '@modules/files/types/files-filters';
import { Folder, type FolderFormGroupConfig } from '@modules/files/types/folder';
import { CROSS_WORKSPACE_DROP_TEXT } from '@modules/files/types/cross-workspace-drop-text';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { StateKey } from '@modules/settings/types/state-key';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { isEqual } from 'lodash-es';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-folder-form',
  templateUrl: './folder-form.component.html',
  styleUrls: ['./folder-form.component.less'],
  standalone: false,
  providers: [
    ...FilesListStateService.providers({
      stateKey: StateKey.filesFFListState,
    }),
    WorkspaceIdentityService,
    provideDetailFooterSlot(),
  ],
})
export class FolderFormComponent
  extends FullFormBaseComponent<StitchType.FOLDER, Folder, FolderFormGroupConfig>
  implements OnInit
{
  public changesKey = 'folder';
  public tabs: Tab[] = [
    { title: 'Files', value: 'folder' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'folders';
  public parentSuggestions: AutocompleteFactory<Folder>;
  public filesFilters: FilesFilters;

  protected dragDataType = DragDataTypes.folder;
  protected override childItemDragType = DragDataTypes.file;
  protected override childItemContainerIdField = 'folderId';

  @Input() folder = new Folder();

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(FilesAppStateService, { appLabel: APP_CRUMBS.files });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Files app › Folders › this folder. */
  protected breadcrumbs(): DetailCrumb[] {
    return this.navigation.crumbs({
      view: TREE_CRUMBS.folders,
      fallbackView: TREE_CRUMBS.folders,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }

  private readonly filesTenantScopeService = inject(FilesTenantScopeService, { optional: true });
  private readonly workspaceIdentityService = inject(WorkspaceIdentityService);

  /** The accepted team identity, or undefined when this form is mounted in a private profile. */
  protected readonly workspaceIdentity = signal<WorkspaceIdentity | undefined>(undefined);

  /** The tenant selected for a new folder; saved folders always keep their own accountId. */
  protected readonly sharingTenantId = signal<string | null>(null);

  /** Makes the primary action state the destination while the picker is present. */
  protected readonly saveLabel = computed(() => {
    const identity = this.workspaceIdentity();
    if (!identity) {
      return 'Save';
    }
    return this.sharingTenantId() === identity.spaceTenantId ? 'Save to team workspace' : 'Save to my workspace';
  });

  private readonly filesListStateService = inject(FilesListStateService, { self: true });
  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.folder,
      childItemType: DragDataTypes.file,
      childService: this.filesService,
      containerIdField: 'folderId',
      appStateService: this.stateService,
      spaceId: this.stitchItem.accountId ?? this.spaceContextStore.contextId(),
      crossTenantCopySupported: true,
      cancelled: this.alive,
      isActive: () => !this.alive.isStopped,
    };
  }

  constructor(
    private foldersService: FoldersService,
    private filesService: FilesService,
    private stateService: FilesAppStateService,
    injector: Injector,
  ) {
    super(injector, foldersService);

    this.sharingTenantId.set(this.filesTenantScopeService?.effectiveTenantId() ?? null);
    this.workspaceIdentityService
      .current()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((identity) => {
        this.workspaceIdentity.set(identity);
        if (!this.stitchItem?.id && identity) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ??
              this.suppliedTenantId() ??
              this.filesTenantScopeService?.effectiveTenantId() ??
              identity.memberTenantId,
          );
        }
      });
  }

  /** A new attachment follows the picker; an existing folder remains addressed by its item owner. */
  protected override tenantOwnerId(): string | undefined {
    return this.stitchItem?.accountId ?? this.sharingTenantId() ?? undefined;
  }

  /** A saved item wins over both the picker and the ambient rail selection. */
  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) {
      return { tenantItems: [this.stitchItem] };
    }

    const tenantId = this.sharingTenantId();
    if (!tenantId) {
      return super.requestConfig();
    }
    return { requestContext: new HttpContext().set(OWNING_SPACE_ID, tenantId) };
  }

  private suppliedTenantId(): string | undefined {
    return this.requestContext?.get(OWNING_SPACE_ID) ?? this.requestContext?.get(MAILBOX_TENANT_ID) ?? undefined;
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if ((changes.folder || changes.requestContext) && !this.stitchItem?.id) {
      this.sharingTenantId.set(
        this.stitchItem?.accountId ??
          this.suppliedTenantId() ??
          this.filesTenantScopeService?.effectiveTenantId() ??
          this.workspaceIdentity()?.memberTenantId ??
          null,
      );
    }
    super.ngOnChanges(changes);
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    super.ngOnInit();

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = this.foldersService.getAutocompleteSuggestions({ exceptIds: [this.stitchItem.id] });
    });

    this.bindFilesFilters();
  }

  /**
   * Keeps the open folder's embedded list stable when its app-local list-state service merely restores
   * the same saved values during the first rail switch. The second-panel list deliberately follows that
   * switch; this tab does not. Comparing the finished filters here preserves both real popover changes
   * and the account-zone correction required by SBUX-829.
   */
  private bindFilesFilters(): void {
    this.stitchItemChanged
      .pipe(
        switchMap(() =>
          combineLatest([this.filesListStateService.value(), this.displayTimeZone$]).pipe(
            map(([state, displayTimeZone]) => {
              // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
              // zone (SBUX-829).
              const filters = new FilesFilters({ containersIds: [this.stitchItem.id], displayTimeZone });
              return filters.applyListState(state);
            }),
            distinctUntilChanged((previous, current) => isEqual(previous, current)),
          ),
        ),
        takeUntil(this.alive),
      )
      .subscribe((filters) => (this.filesFilters = filters));
  }

  /**
   * Actions
   */

  dndDrop(dragData: DragData): void {
    if (this.stitchItem.id && dragData.type !== DragDataTypes.file && this.isCrossWorkspaceDrop(dragData)) {
      this.showCrossWorkspaceDropDialog(dragData);
      return;
    }

    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  /** The open item is the destination authority; the ambient rail selection is irrelevant. */
  private isCrossWorkspaceDrop(dragData: DragData): boolean {
    const sourceTenantId = getStringField(dragData.data[0], 'accountId');
    const destinationTenantId = this.stitchItem.accountId;
    return sourceTenantId !== undefined && destinationTenantId !== undefined && sourceTenantId !== destinationTenantId;
  }

  private showCrossWorkspaceDropDialog(dragData: DragData): void {
    this.alertService.show({
      title: 'Copy, Move or Stitch?',
      body: 'What do we do with the dropped item?',
      note:
        dragData.type === DragDataTypes.folder
          ? CROSS_WORKSPACE_DROP_TEXT.folderNote
          : dragData.type === DragDataTypes.file
            ? CROSS_WORKSPACE_DROP_TEXT.fileNote
            : CROSS_WORKSPACE_DROP_TEXT.itemNote,
      rightButtons: this.getCrossWorkspaceDropButtons(dragData),
    });
  }

  /** Disabled actions intentionally have no callback, so losing the attribute still cannot write. */
  private getCrossWorkspaceDropButtons(dragData: DragData): AlertButtonConfig[] {
    return [
      { title: 'Cancel', close: true },
      {
        title: 'Copy',
        disabled: true,
        tooltip:
          dragData.type === DragDataTypes.folder
            ? CROSS_WORKSPACE_DROP_TEXT.folderCopyTooltip
            : CROSS_WORKSPACE_DROP_TEXT.fileCopyTooltip,
        close: true,
      },
      {
        title: 'Move',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.moveTooltip,
        close: true,
      },
      {
        title: 'Stitch',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.stitchTooltip,
        close: true,
      },
    ];
  }

  /**
   * Helpers
   */

  get stitchItem(): Folder {
    return this.folder;
  }

  protected shouldRefreshList(prev, current) {
    return Folder.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Folder {
    return Folder.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.folder.asFormGroup({
      foldersService: this.foldersService,
      maxAttachmentSize: this.maxUploadSize(),
    });
  }
}
